import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/common/ProtectedRoute";
import logo from "@/assets/logo.png";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Layouts
import PublicLayout from "@/components/layout/PublicLayout";
import AdminLayout from "@/components/layout/AdminLayout";
import CustomerLayout from "@/components/layout/CustomerLayout";

// Public pages
import Home from "@/pages/public/Home";
import About from "@/pages/public/About";
import Services from "@/pages/public/Services";
import Packages from "@/pages/public/Packages";
import Therapists from "@/pages/public/Therapists";
import Branches from "@/pages/public/Branches";
import BranchDetail from "@/pages/public/BranchDetail";
import Doorstep from "@/pages/public/Doorstep";
import Blog from "@/pages/public/Blog";
import BlogDetail from "@/pages/public/BlogDetail";
import Contact from "@/pages/public/Contact";
import Investor from "@/pages/public/Investor";
import Careers from "@/pages/public/Careers";
import Gallery from "@/pages/public/Gallery";
import Book from "@/pages/public/Book";
import BookingSuccess from "@/pages/public/BookingSuccess";
import Membership from "@/pages/public/Membership";
import PrivacyPolicy from "@/pages/public/PrivacyPolicy";

// Auth
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";

// Customer
import CustomerOverview from "@/pages/customer/CustomerOverview";
import CustomerAppointments from "@/pages/customer/CustomerAppointments";
import CustomerFavourites from "@/pages/customer/CustomerFavourites";
import CustomerProfile from "@/pages/customer/CustomerProfile";
import CustomerWallet from "@/pages/customer/CustomerWallet";

// Admin
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminPOS from "@/pages/admin/AdminPOS";
import AdminTherapists from "@/pages/admin/AdminTherapists";
import AdminCommission from "@/pages/admin/AdminCommission";
import AdminWallets from "@/pages/admin/AdminWallets";
import AdminReports from "@/pages/admin/AdminReports";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminBlacklist from "@/pages/admin/AdminBlacklist";
import AdminCMS from "@/pages/admin/AdminCMS";
import AdminPackagesCMS from "@/pages/admin/AdminPackagesCMS";
import AdminBranchesCMS from "@/pages/admin/AdminBranchesCMS";
import AdminMemberships from "@/pages/admin/AdminMemberships";
import AdminMembershipRequests from "@/pages/admin/AdminMembershipRequests";

function App() {
  useEffect(() => {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.getElementsByTagName("head")[0].appendChild(link);
    }
    link.href = logo;
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster
            theme="dark"
            position="top-right"
            toastOptions={{
              style: {
                background: "#121212",
                border: "1px solid #262626",
                color: "#F5F5F5",
              },
            }}
          />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/membership" element={<Membership />} />
              <Route path="/therapists" element={<Therapists />} />
              <Route path="/branches" element={<Branches />} />
              <Route path="/branches/:slug" element={<BranchDetail />} />
              <Route path="/doorstep" element={<Doorstep />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogDetail />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/investor" element={<Investor />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/book" element={<Book />} />
              <Route path="/book/success/:id" element={<BookingSuccess />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            </Route>

            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Customer */}
            <Route
              element={
                <ProtectedRoute roles={["customer"]}>
                  <CustomerLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/dashboard" element={<CustomerOverview />} />
              <Route path="/dashboard/appointments" element={<CustomerAppointments />} />
              <Route path="/dashboard/wallet" element={<CustomerWallet />} />
              <Route path="/dashboard/favourites" element={<CustomerFavourites />} />
              <Route path="/dashboard/profile" element={<CustomerProfile />} />
            </Route>

            {/* Admin */}
            <Route
              element={
                <ProtectedRoute roles={["master_admin", "branch_admin"]}>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/bookings" element={<AdminBookings />} />
              <Route path="/admin/pos" element={<AdminPOS />} />
              <Route path="/admin/membership-requests" element={<AdminMembershipRequests />} />
              <Route path="/admin/therapists" element={<AdminTherapists />} />
              <Route path="/admin/commission" element={<AdminCommission />} />
              <Route path="/admin/wallets" element={<AdminWallets />} />
              <Route path="/admin/reports" element={<AdminReports />} />
              <Route path="/admin/leads" element={<AdminLeads />} />
              <Route path="/admin/blacklist" element={<AdminBlacklist />} />
              <Route path="/admin/cms" element={<AdminCMS />} />
              <Route path="/admin/memberships" element={<AdminMemberships />} />
              {/* <Route path="/admin/site-settings" element={<AdminSiteSettings />} /> */}
              <Route path="/admin/branches-cms" element={<AdminBranchesCMS />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
